import { AppError } from '@repo/error';
import { errorMessage, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { isAdditionalDocument } from '@repo/shared/lib';
import { DocumentSide, DocumentType, ReportAutofillStatus } from '@repo/shared/types';
import { Button } from '@repo/ui/button';
import { Card } from '@repo/ui/card';
import { cn } from '@repo/ui/lib/utils';
import { Separator } from '@repo/ui/separator';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  FileIcon,
  Loader2,
  RefreshCw,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useParams, useRouteLoaderData } from 'react-router';
import { toast } from 'sonner';
import type { WizardLoaderData, WizardReport } from '@/lib/report';
import { deleteReportDocument, uploadReportDocument } from '@/lib/report-documents';
import { trpc } from '@/providers/trpc';
import { useSetUnsavedChanges } from '@/providers/unsaved-changes';
import { useOrganizationStore } from '@/stores/organization-store';
import { AppImage } from '../app-image';

type AdditionalStepProps = {
  onContinue: () => void;
};

type UploadedDoc = NonNullable<WizardReport['documents']>[number];

export function AdditionalStep({ onContinue }: AdditionalStepProps) {
  useLocale();

  const { organizationId = '', reportId = '' } = useParams();
  const currentOrganization = useOrganizationStore((s) => s.currentOrganization);
  const orgId = currentOrganization?.id || organizationId;

  const { report: initialReport } = useRouteLoaderData('report-create-wizard') as WizardLoaderData;

  const { setUnsavedChanges } = useSetUnsavedChanges();
  const [isWaitingForAutofill, setIsWaitingForAutofill] = useState(false);
  const hasMarkedUnsaved = useRef(false);

  const { data: report, refetch } = trpc.report.getReport.useQuery(
    { reportId },
    {
      initialData: initialReport as never,
      refetchInterval: (query) => {
        const data = query.state.data as WizardReport | undefined;
        return isWaitingForAutofill || data?.autofillStatus === ReportAutofillStatus.PROCESSING
          ? 2500
          : false;
      },
    },
  );

  const additionalDocs = (report?.documents ?? []).filter(
    (d): d is UploadedDoc =>
      typeof d === 'object' && d !== null && '_id' in d && isAdditionalDocument(d.documentType),
  );

  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { mutateAsync: triggerAutofillAsync, isPending: isTriggeringAutofill } =
    trpc.report.triggerAutofill.useMutation();

  const { mutate: retryAutofill, isPending: isRetrying } = trpc.report.triggerAutofill.useMutation({
    onError: () => {
      toast.error(t('reports.failedToStartAutofill'), {
        get description() {
          return t('reports.pleaseTryAgain');
        },
      });
    },
  });

  const prevAutofillStatus = useRef(report?.autofillStatus);

  useEffect(() => {
    if (
      (isWaitingForAutofill || prevAutofillStatus.current === ReportAutofillStatus.PROCESSING) &&
      report?.autofillStatus === ReportAutofillStatus.FINISHED
    ) {
      setIsWaitingForAutofill(false);
      onContinue();
    }
    if (report?.autofillStatus === ReportAutofillStatus.FAILED) {
      setIsWaitingForAutofill(false);
    }
    prevAutofillStatus.current = report?.autofillStatus;
  }, [report?.autofillStatus, onContinue, isWaitingForAutofill]);

  const handleDrop = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setIsUploading(true);
      try {
        for (const file of files) {
          await uploadReportDocument(
            orgId,
            reportId,
            file,
            DocumentType.ADDITIONAL,
            DocumentSide.FRONT,
          );
        }
        await refetch();
        if (!hasMarkedUnsaved.current) {
          hasMarkedUnsaved.current = true;
          setUnsavedChanges({
            get message() {
              return t('reports.youHaveAddedContentToThisReportLeavingNow');
            },
            get proceedLinkLabel() {
              return t('extra.leaveAnyway');
            },
          });
        }
        toast.success(
          files.length === 1 ? t('reports.fileUploaded') : `${files.length} files uploaded`,
        );
      } catch (err) {
        toast.error(t('reports.uploadFailed'), {
          description: errorMessage(AppError.parse(err)),
        });
      } finally {
        setIsUploading(false);
      }
    },
    [orgId, reportId, refetch, setUnsavedChanges],
  );

  const handleDelete = async (docId: string) => {
    setDeletingId(docId);
    try {
      await deleteReportDocument(orgId, reportId, docId);
      await refetch();
      toast.success(t('reports.fileRemoved'));
    } catch (err) {
      toast.error(t('reports.failedToRemoveFile'), {
        description: errorMessage(AppError.parse(err)),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    multiple: true,
    disabled: isUploading || report?.autofillStatus === ReportAutofillStatus.PROCESSING,
    onDrop: handleDrop,
    maxSize: 20 * 1024 * 1024,
  });

  const isAutofillProcessing = report?.autofillStatus === ReportAutofillStatus.PROCESSING;
  const isAutofillFailed = report?.autofillStatus === ReportAutofillStatus.FAILED;

  return (
    <div className="relative flex flex-col gap-6">
      {isAutofillFailed && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div className="flex-1 space-y-2">
            <p className="font-medium text-foreground text-sm">
              {t('reports.autofillCouldNotComplete')}
            </p>
            <p className="text-muted-foreground text-sm">
              {t('reports.youCanRetryAutomaticPreFillOrContinueAnd')}{' '}
            </p>
            <Button
              disabled={isRetrying}
              loading={isRetrying}
              onClick={() => retryAutofill({ reportId })}
              size="sm"
              variant="outline"
            >
              <RefreshCw className="me-2 h-4 w-4" />
              {t('reports.retryAutofill')}{' '}
            </Button>
          </div>
        </div>
      )}

      <div
        className={cn(
          'flex flex-col gap-6 transition-[filter,opacity] duration-200',
          isAutofillProcessing && 'pointer-events-none select-none opacity-70 blur-[2px]',
        )}
      >
        <p className="text-muted-foreground text-sm">
          {t('reports.uploadAnySupportingDocumentsCertificatesBankStatementsUtilityBills')}{' '}
        </p>

        {additionalDocs.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
              {t('reports.uploadedFiles')}{' '}
            </p>
            {additionalDocs.map((doc) => (
              <div
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3"
                key={doc._id}
              >
                {doc.mimeType?.startsWith('image/') ? (
                  <AppImage
                    alt={t('reports.documentPreview')}
                    className="h-10 w-10 shrink-0 rounded border border-border object-cover"
                    fileId={doc.fileId}
                    type="thumbnail"
                  />
                ) : (
                  <FileIcon className="h-5 w-5 shrink-0 text-primary" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-sm">
                    {doc.fileName ?? t('extra.additionalDocument')}
                  </p>
                  <p className="text-muted-foreground text-xs">{t('reports.uploaded')}</p>
                </div>
                <Button
                  className="shrink-0"
                  disabled={deletingId === doc._id || isAutofillProcessing}
                  onClick={() => handleDelete(doc._id)}
                  size="icon"
                  variant="ghost"
                >
                  {deletingId === doc._id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-destructive" />
                  )}
                </Button>
              </div>
            ))}
            <Separator />
          </div>
        )}

        <Card
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-3 border-2 border-dashed py-10 transition-colors',
            isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
            isUploading && 'pointer-events-none opacity-60',
          )}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          ) : (
            <UploadCloud className="h-8 w-8 text-muted-foreground/50" />
          )}
          <div className="text-center">
            <p className="font-medium text-foreground text-sm">
              {isUploading
                ? t('reports.uploading')
                : isDragActive
                  ? t('reports.dropFilesHere')
                  : t('reports.dropFilesOrClickToBrowse')}
            </p>
            <p className="mt-0.5 text-muted-foreground text-xs">
              {t('reports.pdfJpgPngWebpMultipleFilesAllowedMax20')}{' '}
            </p>
          </div>
        </Card>

        <div className="flex items-center justify-between pt-2">
          <p className="text-muted-foreground text-xs">
            {additionalDocs.length > 0
              ? t('count.uploaded', { count: additionalDocs.length })
              : t('reports.noAdditionalFilesRequired')}
          </p>
          <Button
            disabled={isAutofillProcessing || isWaitingForAutofill}
            loading={isTriggeringAutofill || isAutofillProcessing || isWaitingForAutofill}
            onClick={async () => {
              try {
                if (report?.autofillStatus === ReportAutofillStatus.FINISHED) {
                  onContinue();
                } else {
                  setIsWaitingForAutofill(true);
                  await triggerAutofillAsync({ reportId });
                }
              } catch {
                setIsWaitingForAutofill(false);
                toast.error(t('reports.failedToStartAutofill'), {
                  get description() {
                    return t('reports.pleaseTryAgain');
                  },
                });
              }
            }}
          >
            {additionalDocs.length === 0 ? t('reports.skip') : t('auth.continue')}
            <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" />
          </Button>
        </div>
      </div>

      {isAutofillProcessing && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl">
          <div className="pointer-events-auto flex flex-col items-center gap-4 rounded-xl border border-border bg-background/90 px-8 py-10 shadow-lg backdrop-blur-sm">
            <motion.div
              animate={{ rotate: 360 }}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10"
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            >
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
            </motion.div>
            <div className="max-w-xs space-y-1 text-center">
              <p className="font-semibold text-foreground">{t('reports.analysingYourDocuments')}</p>
              <p className="text-muted-foreground text-sm">
                {t('reports.weRePreFillingTheNextStepYouLl')}{' '}
              </p>
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <motion.div
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  className="h-2 w-2 rounded-full bg-primary"
                  key={i}
                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3 }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
